import React, { useEffect, useMemo } from 'react';
import { Badge, Space, Tag } from "antd";
import Table from "../../../../componets/Table";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { ColumnsType } from "antd/es/table";
import { AccountInterface } from "../../../../store/mine_craft/accountsSlice";
import { DeviceInterface } from "../../../../store/mine_craft/devicesSlice";
import { fetchBannedIps } from "../../../../actions/mine_craft/bannedIpsActions";
import { formattedDate } from "../../../../helpers/DateHelpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "../../../../componets/Buttons";
import { StopOutlined } from "@ant-design/icons";
import { setBanId } from "../../../../store/mine_craft/devicesSlice";
import Block from "../../Devices/Block";
import Title from '../../Devices/_partials/_Title';
import Meta from "../../Users/_partials/_Meta";
import OptionalLink from "../../../../componets/OptionalLink";
import RowInfo from "../../../../componets/RowInfo";
import '../style.scss';

interface ListTableInterface {
  className?: string;
}

const _ListTable = ({ className }: ListTableInterface) => {
  const ids      = useAppSelector(state => state.mineCraft.accounts.ids);
  const accounts = useAppSelector(state => state.mineCraft.accounts.entities);
  const devices  = useAppSelector(state => state.mineCraft.devices.entities);
  const users    = useAppSelector(state => state.mineCraft.users.entities);
  const bannedIpEntities = useAppSelector(state => state.mineCraft.bannedIps.entities);
  const bannedIpIds      = useAppSelector(state => state.mineCraft.bannedIps.ids);
  const data     = ids ? ids.map(id => accounts[id]) : [];
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(fetchBannedIps({ active: 'true' }));
  }, [dispatch]);

  const bannedIpSet = useMemo(() => {
    const set = new Set<string>();
    if (bannedIpIds) {
      bannedIpIds.forEach(id => {
        const entry = bannedIpEntities[id];
        if (entry?.ip) set.add(entry.ip);
      });
    }
    return set;
  }, [bannedIpIds, bannedIpEntities]);

  function onClickBan(deviceId: number) {
    const device = devices[deviceId];

    if (device) {
      dispatch(setBanId([device.id]));
    }
  }

  const Icon = ({ icon }: { icon: any }) => <FontAwesomeIcon icon={icon} className='v-mr-5' />;

  const renderDeviceType = (device_id: number) => {
    const device = devices[device_id];
    if (!device) return null;
    return device.device_type === 'iPad'
      ? <Icon icon='fa-solid fa-tablet' />
      : <Icon icon='fa-solid fa-mobile' />;
  }

  const renderDevice = (device_id: number) => (
    <>
      {renderDeviceType(device_id)}
      <small>{<Title name={device_id} bannedUntil={devices[device_id]?.banned_until} />}</small>
    </>
  )

  const renderUser = (user_id: number | null) => {
    const user = user_id ? users[user_id] : undefined;
    if (!user) return <span>-</span>;
    return <Meta user={user} size='xsmall' className='v-account-list--meta-user' />
  }

  const renderStatus = (login: boolean, user_id: number | null) => (
    <Space size={4} direction='vertical'>
      <Badge
        status={login ? 'success' : 'default'}
        text={<small>{login ? 'Signed in' : 'Signed out'}</small>}
      />
      <Tag color={user_id ? 'green' : 'orange'}>{user_id ? 'User' : 'Guest'}</Tag>
    </Space>
  )

  const renderIp = (ip: string | null | undefined) => {
    if (!ip) return <small>—</small>;
    const banned = bannedIpSet.has(ip);
    return (
      <span className='v-account-list--ip-cell'>
        <small className='v-account-list--ip'>{ip}</small>
        {banned && <Tag color="error" className='v-account-list--banned-tag'>Banned</Tag>}
      </span>
    );
  };

  const renderTimestamp = (value: string | null | undefined) => (
    value ? <small>{formattedDate(value).dateTime}</small> : <small>—</small>
  );

  const renderIpChain = (device: DeviceInterface) => {
    const steps: Array<{ ip: string; current?: boolean }> = [];
    if (device.ip)        steps.push({ ip: device.ip, current: true });
    if (device.last_ip)   steps.push({ ip: device.last_ip });
    if (device.create_ip) steps.push({ ip: device.create_ip });

    if (steps.length === 0) {
      return <small>—</small>;
    }

    return (
      <div className='v-account-list--ip-chain'>
        {steps.map((step, i) => {
          const banned = bannedIpSet.has(step.ip);
          return (
            <React.Fragment key={`${step.ip}-${i}`}>
              {i > 0 && <span className='v-account-list--ip-chain-arrow'>▸</span>}
              <span className={`v-account-list--ip-chain-item${step.current ? ' is-current' : ''}${banned ? ' is-banned' : ''}`}>
                <span className='v-account-list--ip-value'>{step.ip}</span>
                {step.current && <Tag color="processing" className='v-account-list--ip-current-tag'>Current</Tag>}
                {banned && <Tag color="error" className='v-account-list--ip-current-tag'>Banned</Tag>}
              </span>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const renderExpanded = (account: AccountInterface) => {
    const device = devices[account.device_id];
    if (!device) return null;

    return (
      <div className='v-account-list--details'>
        <div className='v-account-list--details-section'>
          <h6 className='v-content-label o-sm'>Device Details</h6>
          <RowInfo
            left='UID'
            right={account.uid ? <Tag color="processing">{account.uid.toUpperCase()}</Tag> : '—'}
          />
          <RowInfo left='Vendor ID'    right={device.vendor ? device.vendor.toUpperCase() : '—'} />
          <RowInfo left='Launch Count' right={device.launch_count} />
          <RowInfo
            left='Launch At'
            right={device.launch_at ? formattedDate(device.launch_at).dateTime : '—'}
          />
        </div>

        <div className='v-account-list--details-section'>
          <h6 className='v-content-label o-sm'>Sign-in History</h6>
          <RowInfo
            left='Signed In At'
            right={account.sign_at ? formattedDate(account.sign_at).dateTime : '—'}
          />
          <RowInfo
            left='Signed Out At'
            right={account.sign_out_at ? formattedDate(account.sign_out_at).dateTime : '—'}
          />
        </div>

        <div className='v-account-list--details-section v-account-list--details-section-separated'>
          <h6 className='v-content-label o-sm'>IP History (Latest First)</h6>
          {renderIpChain(device)}
        </div>
      </div>
    );
  }

  const columns: ColumnsType<AccountInterface> = [
    {
      key: 'id',
      title: 'Id',
      render: (_, { id }) => <Tag>{id}</Tag>,
      width: 80
    },

    {
      key: 'status',
      title: 'Status',
      render: (_, { login, user_id }) => renderStatus(login, user_id),
      width: 110
    },

    {
      key: 'device_type',
      title: 'Device',
      render: (_, { device_id }) => <OptionalLink link={devices[device_id]?.url}>{renderDevice(device_id)}</OptionalLink>,
      width: 110
    },

    {
      key: 'user_id',
      title: 'User',
      render: (_, { user_id }) => renderUser(user_id),
      width: 180
    },

    {
      key: 'reg_sign_in_ip',
      title: 'Reg Sign-in IP',
      render: (_, { user_id }) => renderIp(user_id ? users[user_id]?.reg_sign_in_ip : null),
      width: 130
    },

    {
      key: 'ip',
      title: 'IP',
      render: (_, { device_id }) => renderIp(devices[device_id]?.ip),
      width: 130
    },

    {
      key: 'last_ip',
      title: 'Last IP',
      render: (_, { device_id }) => renderIp(devices[device_id]?.last_ip),
      width: 130
    },

    {
      key: 'create_ip',
      title: 'Create IP',
      render: (_, { device_id }) => renderIp(devices[device_id]?.create_ip),
      width: 130
    },

    {
      key: 'created_at',
      title: 'Created at',
      render: (_, { created_at }) => renderTimestamp(created_at),
      width: 120
    },

    {
      key: 'action',
      title: 'Action',
      render: (_, { device_id }) => (
        <Space size="middle">
          <Button key='button-ban' onClick={() => onClickBan(device_id)} title='Block' icon={StopOutlined} />
        </Space>
      ),
      width: 50
    },
  ];

  return (
    <div className={className}>
      <Table
        rowKey='id'
        columns={columns}
        dataSource={data}
        expandable={{
          expandedRowRender: renderExpanded,
          rowExpandable: ({ device_id }) => !!devices[device_id],
        }}
      />

      <Block />
    </div>
  )
}

export default _ListTable;
